import LoadFailure from '../../../components/Brand/LoadFailure';
import { EditIcon, SaveIcon } from '../../../components/Brand/Icon';
import usePageLayout from '../../../hooks/usePageLayout';
import '../UserResponsive.scss';
import './Profile.scss';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Button, Input, Alert } from 'antd';
import BrandLoader from '../../../components/Brand/BrandLoader';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import ButtonShare from '../../../components/Buttons/ButtonShare';
import ButtonSettings from '../../../components/Buttons/ButtonSettings';
import ButtonSubscribe from '../../../components/Buttons/ButtonSubscribe';
import ButtonFollowers from '../../../components/Buttons/ButtonFollowers';
import ButtonFollowing from '../../../components/Buttons/ButtonFollowing';
import UserImage from '../../../components/UserImage';
import StoryTitle from '../../../components/StoryTitle';
import WiwHeaderTitle from '../../../components/WiwHeaderTitle';
import { createName } from '../../../utils/func';
import api from '../../../services';
import { getUser } from '../../../utils/func';
import { APP_ROUTES } from '../../../utils/constant';
import url from '../../../utils/url';
import ProfileBell from '../../../notifications/ProfileBell';
import { useNotifications } from '../../../notifications/NotificationsProvider';

const { Header, Content, Footer } = Layout;
const { TextArea } = Input;
const { userByUsername, userUpdateFields, storyList } = api;

export default function UserProfile() {
	let storageUser = getUser();
	const [result, setResult] = useState({ username: null, user: null, reactions: [], loading: true, error: false });
	const [attempt, setAttempt] = useState(0);
	const generation = useRef(0);
	const readController = useRef(null);
	const historyPending = useRef(false);
	const savingRef = useRef(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(false);
	let { username } = useParams();
	const [about, setAbout] = useState(null);
	const [editAbout, setEditAbout] = useState(false);
	const aboutInput = useRef(null);
	const current = result.username === username ? result : { user: null, reactions: [], loading: true };
	const user = current.user || {};
	const reactions = current.reactions;
	const [followingCount, setFollowingCount] = useState('');
	const [followingInitials, setFollowingInitials] = useState('');
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isOwnProfile = Boolean(storageUser && storageUser.id === user.id);
	const { enabled: notificationsEnabled } = useNotifications();
	const aboutLabel = t('settings:aboutYou', { defaultValue: 'About you' });

	const ref_header = useRef(null);
	const ref_footer = useRef(null);

	const { contentHeight, contentTop } = usePageLayout(ref_header, ref_footer);

	// A history retry belongs to the loaded author; it must not restart their
	// profile request or discard an in-progress biography edit.
	const loadHistory = useCallback(async (profileUser, request) => {
		if (generation.current !== request || historyPending.current) return;
		historyPending.current = true;
		setResult(previous => ({ ...previous, loading: true, error: false }));
		try {
			const response = await storyList({ user_id: profileUser.id, activity: true }, { signal: readController.current?.signal });
			if (generation.current !== request) return;
			if (!response?.success || !Array.isArray(response.list)) throw new Error('History unavailable');
			setResult(previous => ({ ...previous, reactions: response.list, loading: false, error: false }));
		} catch {
			if (generation.current === request) setResult(previous => ({ ...previous, loading: false, error: true }));
		} finally {
			if (generation.current === request) historyPending.current = false;
		}
	}, []);

	useEffect(() => {
		const request = ++generation.current;
		const controller = new AbortController();
		readController.current = controller;
		historyPending.current = false;
		savingRef.current = false;
		setSaving(false);
		setSaveError(false);
		setEditAbout(false);
		setAbout('');
		setResult({ username, user: null, reactions: [], loading: true, error: false });
		const load = async () => {
			try {
				const response = await userByUsername({ username }, { signal: controller.signal });
				if (generation.current !== request) return;
				if (response?.statusCode === 404 || (response?.success && !response.user)) {
					navigate(url(APP_ROUTES.PAGE404), { replace: true });
					return;
				}
				if (!response?.success || !response.user) throw new Error('Profile unavailable');
				setFollowingCount(Number(response.user.followers_count) || 0);
				setFollowingInitials(Boolean(response.user.following));
				setAbout(response.user.about || '');
				// The author is ready; their history must not hold the whole
				// profile behind a second network round trip.
				setResult({ username, user: response.user, reactions: [], loading: true, error: false });
				await loadHistory(response.user, request);
			} catch {
				if (generation.current === request) setResult(previous => ({ ...previous, loading: false, error: true }));
			}
		};
		load();
		return () => { controller.abort(); if (generation.current === request) generation.current += 1; };
	}, [username, attempt, navigate, loadHistory]);

	const retryLoad = () => {
		if (current.user) loadHistory(current.user, generation.current);
		else setAttempt(value => value + 1);
	};

	const pageTitle = current.user ? `${t('user.profile_title')} ${createName(user)} @${user.user_name}` : t('user.profile_title');
	const pageDescription = current.user ? t('user.profile_description', { user: `${createName(user)} @${user.user_name}` }) : pageTitle;

	const onChangeAbout = (e) => {
		setAbout(e.target.value);
	}

	const startEditAbout = () => {
		// Mount and focus during the tap so mobile Safari can open its keyboard.
		flushSync(() => setEditAbout(true));
		aboutInput.current?.focus({ cursor: 'end', preventScroll: true });
	}

	const endEditAbout = async () => {
		if (savingRef.current) return;
		const request = generation.current;
		savingRef.current = true;
		setSaving(true);
		setSaveError(false);
		try {
			const response = await userUpdateFields({ id: user.id, about });
			if (generation.current !== request) return;
			if (!response?.success) throw new Error('Save failed');
			setEditAbout(false);
		} catch {
			if (generation.current === request) setSaveError(true);
		} finally {
			if (generation.current === request) { savingRef.current = false; setSaving(false); }
		}
	}

	const handleFollowChange = (isFollowing) => {
		setSaveError(false);
		setFollowingCount(prev => Math.max(0, (Number(prev) || 0) + (isFollowing ? 1 : -1)));
		setFollowingInitials(isFollowing);
	};

	const profileButtons = (() => {
		if (isOwnProfile) {
			return <>
				{editAbout ? (
					<Button type="text" aria-label={t('user.save_settings')} disabled={saving} aria-busy={saving} icon={<SaveIcon />} onClick={endEditAbout} />
				) : (
					<Button type="text" aria-label={t('user.edit_profile', { defaultValue: 'Edit profile' })} icon={<EditIcon />} onClick={startEditAbout} />
				)}
				<ProfileBell userId={user.id} own onError={() => setSaveError(true)} />
				<ButtonSettings />

			</>
		} else {
			return <>
				<ButtonSubscribe
					user_id={user.id}
					initialIsFollowing={followingInitials}
					onFollowChange={handleFollowChange}
                    onFollowError={() => setSaveError(true)}
				/>
				<ProfileBell userId={user.id} onError={() => setSaveError(true)} />
			</>
		}
	})();

	const reactionItems = reactions.filter(reaction => reaction?.story?.id).map(reaction =>
		<StoryTitle key={reaction.story.id} storyId={reaction.story.id} title={reaction.story.title}
			msg={reaction.text_msg} scope={reaction.scope} reactionId={reaction.id} opinion={reaction} profileUsername={username} />
	);

	return (
		<Layout className="app-layout user-page user-profile-page">
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
			</Helmet>
			<Header className="header" ref={ref_header}>
				<WiwHeaderTitle title={user.user_name || username} preserveCase />
				{current.user && <div className="wrapper-padding">
					<div className="profile-user-control">
						<div className="profile-user-avatar">
							<UserImage picture={user.picture} />
						</div>
						<div className="profile-user-control-button" style={{ '--profile-action-count': (isOwnProfile ? 5 : 4) + (notificationsEnabled ? 1 : 0) }}>
								{profileButtons}
								<ButtonFollowers username={username} count={followingCount} />
								<ButtonFollowing username={username} count={user.following_count} />
								<ButtonShare shareContext={{ source: 'profile', targetType: 'profile', targetId: user.id }} />
						</div>
					</div>
						<div className="profile-user-name">
							{createName(user)}
						</div>
				</div>}
			</Header>
			<Content className="content-layot profile_page wrapper-padding" style={{ height: contentHeight, top: contentTop }}>
				{current.loading && !current.user && <BrandLoader />}
				{current.error && <LoadFailure onRetry={retryLoad} />}
				{saveError && <Alert type="error" role="alert" message={t('brand:saveError')} />}
				{current.user && <div className="profile-bio">
					{editAbout ? (
						<TextArea ref={aboutInput} className="profileAbout" aria-label={aboutLabel} disabled={saving} autoSize={{ minRows: 2, maxRows: 8 }} value={about} placeholder={aboutLabel} onChange={onChangeAbout} />
					) : (
						about?.trim() || !isOwnProfile ? <>{about}</> : <span className="profile-bio-placeholder">{aboutLabel}</span>
					)}
				</div>}
				<div className="profile-events" aria-busy={current.loading}>
					{current.loading && current.user && <BrandLoader />}
					{reactionItems}
					{current.user && !current.loading && !current.error && !reactionItems.length && <p className="profile-events-empty" role="status">
						{isOwnProfile
							? t('settings:noActivity', { defaultValue: 'Your opinions will appear here' })
							: t('settings:noPublicOpinions', { defaultValue: 'No public opinions yet' })}
					</p>}
				</div>
			</Content>
			<Footer ref={ref_footer}>
				<ButtonPanelReaction />
			</Footer>
		</Layout>
	)
}
